import moment from 'moment-timezone';
import { ORDER_STATUS } from 'src/constants/common';

import { getScheduleColumnValues } from '../Orders/schedule.helpers';

import { TRACK_COLUMN_CLASSNAME_MAP, TRACK_COLUMN_TRANSLATION_MAP, TRACK_DATE_PRESET, TRACK_DATE_PRESET_OPTIONS, TRACK_DEFAULT_COLUMNS, TRACK_SORTABLE_COLUMNS } from './track.const';
import { ITrackAppliedFilters, ITrackAppliedSorting, ITrackParams, ITrackTab } from './track.interface';

// A leg that hasn't started moving yet (booked/dispatched) can still carry stale or
// pre-entered manual pings — those must NEVER drive the truck marker, the traveled/remaining
// route split, or route waypoints, or the map jumps ahead of the freight the moment the
// master order flips to in-transit.
const hasLegStartedMoving = (leg: any) => ![ORDER_STATUS.BOOKED, ORDER_STATUS.DISPATCHED].includes(leg?.orderStatus);

// Route waypoints: pings from STARTED legs only, sorted newest-first GLOBALLY (the route
// fetchers expect newest-first and reverse internally). Concatenating per-leg arrays
// without the global sort interleaves leg order with within-leg newest-first, so the
// reversed route zigzags between legs and the measured distance balloons.
export const getStartedLegLocationUpdates = (legs: any[]) =>
    legs
        .filter(hasLegStartedMoving)
        .flatMap((leg: any) => leg?.locationUpdates ?? [])
        .filter((u: any) => u?.latitude && u?.longitude)
        .sort((a: any, b: any) => new Date(b?.createdAt ?? 0).getTime() - new Date(a?.createdAt ?? 0).getTime());

// Truck position: the newest ping BY TIMESTAMP across started legs — never by leg position,
// which would prefer a later leg's older ping over the active leg's fresh one.
export const getLatestStartedLegLocation = (legs: any[]) =>
    legs
        .filter(hasLegStartedMoving)
        .map((leg: any) => leg?.lastLocationUpdate)
        .filter((u: any) => u?.latitude && u?.longitude)
        .sort((a: any, b: any) => new Date(b?.createdAt ?? 0).getTime() - new Date(a?.createdAt ?? 0).getTime())[0] ?? null;

const TRACK_KNOWN_COLUMN_IDS = new Set(TRACK_DEFAULT_COLUMNS.map((col) => col.id));

// Column settings are persisted per user, so ids that no longer exist in the defaults (the retired
// combined `date` column) can still arrive from the API. They have no header label and no row cell,
// so they are dropped here rather than rendering an empty column.
const dropRetiredTrackColumns = (columns: any[]): any[] => (columns ?? []).filter((column: any) => TRACK_KNOWN_COLUMN_IDS.has(column?.id));

export const transformTrackColumnsToHeadCells = (columns: any[], _activeTab: string, hasData: boolean, t: (key: string) => string) => {
    const sortedColumns = dropRetiredTrackColumns(columns).sort((a, b) => a.displayOrder - b.displayOrder);

    return sortedColumns.map((column) => ({
        ...column,
        _columnId: column.id,
        name: TRACK_COLUMN_TRANSLATION_MAP[column.id] ? t(TRACK_COLUMN_TRANSLATION_MAP[column.id]) : column.name,
        // Only columns the API can order by are clickable — Type/Time are derived values.
        sortable: hasData && TRACK_SORTABLE_COLUMNS.has(column.id),
        rowClassName: TRACK_COLUMN_CLASSNAME_MAP[column.id] || '',
    }));
};

export const formatTrackRowData = (trackList: any[], _activeTab: string, t: (key: string) => string) => {
    if (!trackList.length) return [];

    const first = trackList[0];
    // Same derivation the real row uses, so the column-arrange preview matches the table.
    const pickup = getScheduleColumnValues(first?.schedule, 'pickup', t);
    const delivery = getScheduleColumnValues(first?.schedule, 'delivery', t);

    return [
        [
            {
                id: 'orderId',
                name: 'Order ID',
                data: { masterOrderFullId: first?.masterOrderFullId },
            },
            {
                id: 'carrierName',
                name: 'Carrier',
                data: {
                    carrierImage: first?.carrier?.image,
                    carrierName: first?.carrier?.name,
                },
            },
            {
                id: 'orderType',
                name: 'Type',
                data: { orderType: first?.orderType },
            },
            {
                id: 'orderStatus',
                name: 'Status',
                data: { orderStatus: first?.orderStatus },
            },
            { id: 'pickupDate', name: 'Pickup Date', data: { date: pickup.date, datePlain: pickup.datePlain } },
            { id: 'pickupType', name: 'Pickup Type', data: { text: pickup.type } },
            { id: 'pickupTime', name: 'Pickup Time', data: { text: pickup.time } },
            { id: 'deliveryDate', name: 'Delivery Date', data: { date: delivery.date, datePlain: delivery.datePlain } },
            { id: 'deliveryType', name: 'Delivery Type', data: { text: delivery.type } },
            { id: 'deliveryTime', name: 'Delivery Time', data: { text: delivery.time } },
            {
                id: 'shipperCity',
                name: 'From',
                data: {
                    companyName: first?.originAddress?.companyName,
                    city: first?.originAddress?.city,
                    stateCode: first?.originAddress?.stateCode,
                    postal: first?.originAddress?.postal,
                },
            },
            {
                id: 'consigneeCity',
                name: 'To',
                data: {
                    companyName: first?.destinationAddress?.companyName,
                    city: first?.destinationAddress?.city,
                    stateCode: first?.destinationAddress?.stateCode,
                    postal: first?.destinationAddress?.postal,
                },
            },
        ],
    ];
};

export const isTrackParamsChanged = (columnsOrder: any[], trackColumns: any[]): boolean => {
    if (!columnsOrder || columnsOrder.length === 0) return false;

    return columnsOrder.some((col: any) => {
        const originalCol = trackColumns.find((c: any) => c.id === col.id);
        if (!originalCol) return true;

        return col.displayOrder !== originalCol.displayOrder || col.visible !== originalCol.visible;
    });
};

export const mergeTrackColumnsWithDefaults = (savedColumnsRaw: any[]): any[] => {
    const savedColumns = dropRetiredTrackColumns(savedColumnsRaw);
    const savedIds = new Set(savedColumns.map((s: any) => s.id));
    const missing = TRACK_DEFAULT_COLUMNS.filter((col) => !savedIds.has(col.id));

    if (!missing.length) return savedColumns;

    const maxOrder = Math.max(0, ...savedColumns.map((s: any) => s.displayOrder ?? 0));
    const withOrder = missing.map((col, i) => ({ ...col, displayOrder: maxOrder + i + 1 }));

    return [...savedColumns, ...withOrder];
};

// Mirrors wal-frontend-v2's getRadarActionColumnWidthClass: size the action column to the icons the
// row actually renders (priority button always, bell only when the text-notification feature is
// visible), so the column hugs its content. Keep these counts in sync with TrackTableRow's action cell.
const TRACK_ACTION_ICON_PX = 16; // each icon button (w-4)
const TRACK_ACTION_GAP_PX = 8; // gap-x-2 between icons
const TRACK_ACTION_PADDING_PX = 24; // 12px left + 12px right (table-col-tooltip-space-common-xs)

export const getTrackActionColumnWidthClass = (trackList: any[], isTextNotificationFeatureVisible: boolean): string => {
    if (!trackList?.length) return '';

    const iconCount = 1 + (isTextNotificationFeatureVisible ? 1 : 0);
    const contentWidth = iconCount * TRACK_ACTION_ICON_PX + (iconCount - 1) * TRACK_ACTION_GAP_PX;
    const maxWidth = contentWidth + TRACK_ACTION_PADDING_PX;

    return `min-w-[${maxWidth}px] w-[${maxWidth}px] max-w-[${maxWidth}px]`;
};

// ── Quick date-range helpers ─────────────────────────────────────────────────
// Ported 1:1 from WAL radar (wal-frontend-v2 src/app/Radar/radar.helpers.ts:1227-1346).
// trackParams.startDate/endDate are "YYYY-MM-DD"; trim defensively in case a
// "YYYY-MM-DD HH:mm:ss" form ever reaches these (WAL persists that shape).
const toDateOnly = (value: any) => (value ? String(value).slice(0, 10) : '');

// Resolve a preset to trackParams.startDate / endDate strings ("YYYY-MM-DD").
// Anchored in the user's timezone so "today" matches what the user sees, not UTC.
// Week starts Sunday to match the calendar's own Su-first header.
export const getTrackPresetDateRange = (preset: string, userTimezone: string) => {
    const today = moment().tz(userTimezone);

    switch (preset) {
        case TRACK_DATE_PRESET.tomorrow: {
            const tomorrow = today.clone().add(1, 'day');

            return { startDate: tomorrow.format('YYYY-MM-DD'), endDate: tomorrow.format('YYYY-MM-DD') };
        }

        case TRACK_DATE_PRESET.thisWeek:
            return {
                startDate: today.clone().startOf('week').format('YYYY-MM-DD'),
                endDate: today.clone().endOf('week').format('YYYY-MM-DD'),
            };

        case TRACK_DATE_PRESET.thisMonth:
            return {
                startDate: today.clone().startOf('month').format('YYYY-MM-DD'),
                endDate: today.clone().endOf('month').format('YYYY-MM-DD'),
            };

        case TRACK_DATE_PRESET.today:
        default:
            return { startDate: today.format('YYYY-MM-DD'), endDate: today.format('YYYY-MM-DD') };
    }
};

// Range-shape tests used to pick the arrow stride. Week is Sunday-first, matching both the
// calendar header and getTrackPresetDateRange.
const isWholeWeek = (start: any, end: any) => start.isSame(start.clone().startOf('week'), 'day') && end.isSame(start.clone().endOf('week'), 'day');

const isWholeMonth = (start: any, end: any) => start.isSame(start.clone().startOf('month'), 'day') && end.isSame(start.clone().endOf('month'), 'day');

// Step the current range one window backwards/forwards. The stride comes from the range's
// own SHAPE, not from any stored preset, so it stays correct however the range was picked
// (dropdown, arrow, or the Custom calendar): a full Sun-Sat week steps a week, a whole
// calendar month steps a month, anything else steps by its own length — so consecutive
// clicks always walk adjacent, non-overlapping windows.
export const shiftTrackDateRange = (startDate: string, endDate: string, direction: 1 | -1) => {
    const start = moment(toDateOnly(startDate), 'YYYY-MM-DD');
    const end = moment(toDateOnly(endDate) || toDateOnly(startDate), 'YYYY-MM-DD');

    if (isWholeWeek(start, end)) {
        const nextStart = start.clone().add(direction, 'week').startOf('week');

        return { startDate: nextStart.format('YYYY-MM-DD'), endDate: nextStart.clone().endOf('week').format('YYYY-MM-DD') };
    }

    if (isWholeMonth(start, end)) {
        const nextStart = start.clone().add(direction, 'month').startOf('month');

        return { startDate: nextStart.format('YYYY-MM-DD'), endDate: nextStart.clone().endOf('month').format('YYYY-MM-DD') };
    }

    // Single day → one day. Any other span → its own length.
    const spanDays = end.diff(start, 'days') + 1;

    return {
        startDate: start
            .clone()
            .add(direction * spanDays, 'days')
            .format('YYYY-MM-DD'),
        endDate: end
            .clone()
            .add(direction * spanDays, 'days')
            .format('YYYY-MM-DD'),
    };
};

// Which preset the CURRENT DATES represent — derived purely from the dates, never from how
// the user got there (dropdown pick, arrow step, or Custom calendar all behave identically).
// "This Week"/"This Month" match only the week/month containing today: next week's exact
// Sun-Sat is a real range, not "This Week", so it falls through to showing its dates.
export const getActiveTrackDatePreset = (params: any, userTimezone: string) => {
    const startDate = toDateOnly(params?.startDate);
    const endDate = toDateOnly(params?.endDate) || startDate;

    if (!startDate) return TRACK_DATE_PRESET.custom;

    const matched = [TRACK_DATE_PRESET.today, TRACK_DATE_PRESET.tomorrow, TRACK_DATE_PRESET.thisWeek, TRACK_DATE_PRESET.thisMonth].find((preset) => {
        const resolved = getTrackPresetDateRange(preset, userTimezone);

        return resolved.startDate === startDate && resolved.endDate === endDate;
    });

    return matched ?? TRACK_DATE_PRESET.custom;
};

// Trigger label: the preset name while it's on-anchor, otherwise the actual date(s).
// Single day → "Aug 31st, 2026"; range → "Aug 31st - Sep 6th, 2026".
export const getTrackDateRangeLabel = (params: any, userTimezone: string, t: any) => {
    const activePreset = getActiveTrackDatePreset(params, userTimezone);
    const presetOption = TRACK_DATE_PRESET_OPTIONS.find((opt) => opt.value === activePreset);

    if (activePreset !== TRACK_DATE_PRESET.custom && presetOption) {
        return String(t(presetOption.labelKey));
    }

    if (!params?.startDate) return '';

    const start = moment(toDateOnly(params.startDate), 'YYYY-MM-DD');
    const end = moment(toDateOnly(params.endDate) || toDateOnly(params.startDate), 'YYYY-MM-DD');

    if (start.isSame(end, 'day')) {
        return start.format('MMM Do, YYYY');
    }

    // Drop the repeated year when both ends land in the same one.
    return start.isSame(end, 'year') ? `${start.format('MMM Do')} - ${end.format('MMM Do, YYYY')}` : `${start.format('MMM Do, YYYY')} - ${end.format('MMM Do, YYYY')}`;
};

// ─────────────────────────────────────────────────────────────────────────────
// Saved-view helpers (14 fields: 12 filters + 2 sorting)
// ─────────────────────────────────────────────────────────────────────────────
// Shared by the table view (tabs / Save View / Cancel) and the map view (Overview card
// view dropdown / filter-modal Save View) so saving, applying and dirty-checking a view
// behave identically everywhere. Ported from wal-frontend-v2 radar.helpers.ts with this
// module's param names. NOTE: `isTrackParamsChanged` above compares COLUMN settings, not
// filters — the filter dirty check is `isTrackViewChanged` below.

export const TRACK_VIEW_SORT_DEFAULTS: ITrackAppliedSorting = { sortField: 'lastUpdatedAt', sortType: 'desc' };

const TRACK_VIEW_FILTER_FIELDS: (keyof ITrackAppliedFilters)[] = [
    'search',
    'refSearch',
    'shipperId',
    'consigneeId',
    'status',
    'serviceType',
    'startDate',
    'endDate',
    'originSearch',
    'destinationSearch',
    'billingAccountId',
    'divisionId',
];

// Normalize a view-field value so equivalent representations compare equal: dates may
// carry a time suffix ("YYYY-MM-DD 00:00:00" from the filter modal), ids arrive as numbers
// from the API but strings from selects, and an empty service type means 'all'.
const normalizeTrackViewValue = (field: keyof ITrackAppliedFilters, value: any): string => {
    if (field === 'startDate' || field === 'endDate') return value ? String(value).slice(0, 10) : '';

    if (field === 'serviceType') return !value || value === 'all' ? 'all' : String(value);

    return value === null || value === undefined ? '' : String(value);
};

/** Dirty check: do the current params differ from what `view` has saved (filters OR sorting)? */
export const isTrackViewChanged = (view: Partial<ITrackTab> | null | undefined, params: Partial<ITrackParams>): boolean => {
    const filtersChanged = TRACK_VIEW_FILTER_FIELDS.some((field) => normalizeTrackViewValue(field, view?.appliedFilters?.[field]) !== normalizeTrackViewValue(field, (params as any)?.[field]));

    const sortingChanged =
        (view?.appliedSorting?.sortField || TRACK_VIEW_SORT_DEFAULTS.sortField) !== (params?.sortField || TRACK_VIEW_SORT_DEFAULTS.sortField) ||
        (view?.appliedSorting?.sortType || TRACK_VIEW_SORT_DEFAULTS.sortType) !== (params?.sortType || TRACK_VIEW_SORT_DEFAULTS.sortType);

    return filtersChanged || sortingChanged;
};

const toNullableNumber = (value: any): number | null => (value === null || value === undefined || value === '' ? null : Number(value));

/** Snapshot params into the appliedFilters/appliedSorting shape views are saved with. */
export const buildAppliedTrackView = (params: Partial<ITrackParams>): { appliedFilters: ITrackAppliedFilters; appliedSorting: ITrackAppliedSorting } => ({
    appliedFilters: {
        search: params?.search || '',
        refSearch: params?.refSearch || '',
        shipperId: params?.shipperId || null,
        consigneeId: params?.consigneeId || null,
        status: params?.status || 'dispatched',
        serviceType: params?.serviceType || 'all',
        startDate: params?.startDate ? String(params.startDate).slice(0, 10) : '',
        endDate: params?.endDate ? String(params.endDate).slice(0, 10) : '',
        originSearch: params?.originSearch || '',
        destinationSearch: params?.destinationSearch || '',
        billingAccountId: toNullableNumber(params?.billingAccountId),
        divisionId: toNullableNumber(params?.divisionId),
    },
    appliedSorting: {
        sortField: params?.sortField || TRACK_VIEW_SORT_DEFAULTS.sortField,
        sortType: params?.sortType || TRACK_VIEW_SORT_DEFAULTS.sortType,
    },
});

/**
 * View → live params (apply a saved view). Dates missing from the view fall back to
 * `today` (the Track default, which reads as "no date filter"); a missing status falls
 * back to the stage the user is on so a view never lands on an empty stage.
 */
export const makeAppliedTrackViewParamsPatch = (
    appliedFilters: Partial<ITrackAppliedFilters> | undefined,
    appliedSorting: Partial<ITrackAppliedSorting> | undefined,
    today: string,
    fallbackStage: string
): Partial<ITrackParams> => ({
    page: 1,
    search: appliedFilters?.search || '',
    refSearch: appliedFilters?.refSearch || '',
    shipperId: appliedFilters?.shipperId || null,
    consigneeId: appliedFilters?.consigneeId || null,
    status: appliedFilters?.status || fallbackStage,
    filterBySubStatus: '',
    serviceType: appliedFilters?.serviceType || 'all',
    startDate: appliedFilters?.startDate ? String(appliedFilters.startDate).slice(0, 10) : today,
    endDate: appliedFilters?.endDate ? String(appliedFilters.endDate).slice(0, 10) : today,
    originSearch: appliedFilters?.originSearch || '',
    destinationSearch: appliedFilters?.destinationSearch || '',
    billingAccountId: toNullableNumber(appliedFilters?.billingAccountId),
    divisionId: toNullableNumber(appliedFilters?.divisionId),
    sortField: appliedSorting?.sortField || TRACK_VIEW_SORT_DEFAULTS.sortField,
    sortType: appliedSorting?.sortType || TRACK_VIEW_SORT_DEFAULTS.sortType,
});

/** Reset every view field to its default for `stage` (what a default stage tab shows). */
export const makeDefaultTrackViewParamsPatch = (stage: string, today: string): Partial<ITrackParams> => ({
    page: 1,
    status: stage,
    filterBySubStatus: '',
    search: '',
    refSearch: '',
    shipperId: null,
    consigneeId: null,
    serviceType: 'all',
    startDate: today,
    endDate: today,
    originSearch: '',
    destinationSearch: '',
    billingAccountId: null,
    divisionId: null,
    sortField: TRACK_VIEW_SORT_DEFAULTS.sortField,
    sortType: TRACK_VIEW_SORT_DEFAULTS.sortType,
});

/** API rows (custom_views) → tabs. Ids are numbers server-side, tab values are strings. */
export const formatTrackTabData = (rows: any[]): ITrackTab[] =>
    (rows ?? []).map((item) => ({
        name: item?.name,
        value: String(item?.id),
        id: item?.id,
        count: item?.count ?? 0,
        appliedFilters: item?.appliedFilters,
        appliedSorting: item?.appliedSorting,
        isIconShow: true,
    }));

/** Unique view names, for the create/rename modal's duplicate check. */
export const extractTrackViewNames = (tabs: ITrackTab[]): string[] => Array.from(new Set(tabs.map((tab) => tab?.name).filter(Boolean)));
